import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineGrid,
  Box,
  InlineStack,
  Badge,
  Button,
  Spinner,
  Banner,
} from '@shopify/polaris';
// Icons available for future use
// import { ProductIcon, PersonIcon, OrderIcon, EmailIcon } from '@shopify/polaris-icons';
import { dashboardService, type DashboardStats, type RecentActivity } from '../../services/dashboard.service';

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  trend?: string;
  loading?: boolean;
}

function MetricCard({ title, value, subtitle, trend, loading }: MetricCardProps) {
  return (
    <Card>
      <BlockStack gap="200">
        <Text as="p" variant="bodyMd" tone="subdued">
          {title}
        </Text>
        {loading ? (
          <Spinner size="small" />
        ) : (
          <>
            <InlineStack align="space-between" blockAlign="center">
              <Text as="p" variant="headingXl" fontWeight="bold">
                {value}
              </Text>
              {trend && (
                <Badge tone={trend.startsWith('+') ? 'success' : trend.startsWith('-') ? 'critical' : 'info'}>
                  {trend}
                </Badge>
              )}
            </InlineStack>
            {subtitle && (
              <Text as="p" variant="bodySm" tone="subdued">
                {subtitle}
              </Text>
            )}
          </>
        )}
      </BlockStack>
    </Card>
  );
}

function formatTimeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  return `${diffDays} days ago`;
}

function getActivityIcon(type: string): string {
  switch (type) {
    case 'user_registered':
      return '👤';
    case 'inquiry_created':
      return '📋';
    case 'contact_submitted':
      return '✉️';
    case 'quote_sent':
      return '💰';
    default:
      return '📌';
  }
}

export function DashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [activities, setActivities] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [statsData, activitiesData] = await Promise.all([
        dashboardService.getStats(),
        dashboardService.getRecentActivities(10),
      ]);
      setStats(statsData);
      setActivities(activitiesData);
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <Page title="Dashboard">
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setError('')}>
              {error}
            </Banner>
          </Layout.Section>
        )}

        {/* Metrics */}
        <Layout.Section>
          <InlineGrid columns={{ xs: 1, sm: 2, md: 4 }} gap="400">
            <MetricCard
              title="Total Products"
              value={stats?.products.total ?? 0}
              subtitle={`${stats?.products.active ?? 0} active, ${stats?.products.draft ?? 0} draft`}
              loading={loading}
            />
            <MetricCard
              title="Total Users"
              value={stats?.users.total ?? 0}
              subtitle={`${stats?.users.newToday ?? 0} new today`}
              trend={stats?.users.newToday ? `+${stats.users.newToday}` : undefined}
              loading={loading}
            />
            <MetricCard
              title="Pending Inquiries"
              value={stats?.inquiries.pending ?? 0}
              subtitle={`${stats?.inquiries.newToday ?? 0} new today`}
              trend={stats?.inquiries.newToday ? `+${stats.inquiries.newToday}` : undefined}
              loading={loading}
            />
            <MetricCard
              title="New Contacts"
              value={stats?.contacts.new ?? 0}
              subtitle={`${stats?.contacts.todayCount ?? 0} today`}
              loading={loading}
            />
          </InlineGrid>
        </Layout.Section>

        {/* Recent Activities */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text as="h2" variant="headingMd">
                  Recent Activities
                </Text>
                <Button variant="plain" onClick={() => navigate('/inquiries')}>
                  View all inquiries
                </Button>
              </InlineStack>
              {loading ? (
                <Box padding="400">
                  <InlineStack align="center">
                    <Spinner size="small" />
                  </InlineStack>
                </Box>
              ) : activities.length === 0 ? (
                <Text as="p" tone="subdued">
                  No recent activities
                </Text>
              ) : (
                <BlockStack gap="200">
                  {activities.map((activity) => (
                    <Box
                      key={activity.id}
                      padding="300"
                      background="bg-surface-secondary"
                      borderRadius="200"
                    >
                      <InlineStack align="space-between" blockAlign="center">
                        <InlineStack gap="300" blockAlign="center">
                          <Text as="span" variant="bodyMd">
                            {getActivityIcon(activity.type)}
                          </Text>
                          <Text as="p" variant="bodyMd">
                            {activity.description}
                          </Text>
                        </InlineStack>
                        <Text as="p" variant="bodySm" tone="subdued">
                          {formatTimeAgo(activity.createdAt)}
                        </Text>
                      </InlineStack>
                    </Box>
                  ))}
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Quick Actions */}
        <Layout.Section variant="oneHalf">
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Quick Actions
              </Text>
              <BlockStack gap="200">
                <Button fullWidth onClick={() => navigate('/products/new')}>
                  Add New Product
                </Button>
                <Button fullWidth onClick={() => navigate('/categories/new')}>
                  Add New Category
                </Button>
                <Button fullWidth onClick={() => navigate('/inquiries?status=PENDING')}>
                  View Pending Inquiries ({String(stats?.inquiries.pending ?? 0)})
                </Button>
                <Button fullWidth onClick={() => navigate('/content/catalogues')}>
                  Manage Catalogues
                </Button>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Summary Cards */}
        <Layout.Section variant="oneHalf">
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Inquiry Summary
              </Text>
              <BlockStack gap="300">
                <InlineStack align="space-between">
                  <Text as="p" variant="bodyMd">
                    Pending
                  </Text>
                  <Badge tone="warning">{String(stats?.inquiries.pending ?? 0)}</Badge>
                </InlineStack>
                <InlineStack align="space-between">
                  <Text as="p" variant="bodyMd">
                    In Progress
                  </Text>
                  <Badge tone="info">{String(stats?.inquiries.inProgress ?? 0)}</Badge>
                </InlineStack>
                <InlineStack align="space-between">
                  <Text as="p" variant="bodyMd">
                    Completed
                  </Text>
                  <Badge tone="success">{String(stats?.inquiries.completed ?? 0)}</Badge>
                </InlineStack>
                <InlineStack align="space-between">
                  <Text as="p" variant="bodyMd">
                    Total
                  </Text>
                  <Badge>{String(stats?.inquiries.total ?? 0)}</Badge>
                </InlineStack>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
